import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Heart } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { Player } from "../components/Player";
import { EmptyState } from "../components/EmptyState";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { db, itemKey } from "../lib/db";
import { Favorites, deriveCatchupStreamId } from "../lib/favorites";
import {
  Resume,
  ensurePageHideFlush,
  flushPendingResume,
  scheduleResumeUpsert,
  syncResumeCache,
} from "../lib/resume";
import {
  Cast as CastApi,
  Playlists,
  Subs,
  Transmux,
  Vod,
  type OpenSubtitleResult,
  type TransmuxInfo,
} from "../lib/api";
import { SubtitleBrowser } from "../components/SubtitleBrowser";
import type { PlaybackSubtitle } from "../contexts/PlaybackContext";
import { decodeMaybeUtf8 as decodeMaybe } from "../lib/decode";
import { safeImg } from "../lib/img";
import { fmtTimeIn, getTimezone } from "../lib/prefs";
import { formatRating } from "../lib/format";
import { usePlayback } from "../contexts/PlaybackContext";
import type { Episode, ShortEpgEntry } from "../lib/types";

interface CatchupProgramState {
  title?: string;
  description?: string;
  startMs?: number;
  endMs?: number;
}

import {
  containerNeedsTranscode as containerNeedsTranscodeFor,
  pickEngine,
  type PlaybackEngine as Engine,
  type PlaybackKind as Kind,
} from "../lib/playback";


// Browser HEVC support: only some Chrome/Edge installs (Windows w/ MS HEVC
// Extensions, macOS, modern Android with HW decoder) can decode hvc1 in MP4
// natively. When supported we'd rather direct-play the original file than
// pay the server-side transcode cost. canPlayType returns "" for "no",
// "maybe" / "probably" for "yes".
let _hevcSupported: boolean | null = null;
function browserCanHevc(): boolean {
  if (_hevcSupported !== null) return _hevcSupported;
  if (typeof document === "undefined") return false;
  try {
    const v = document.createElement("video");
    const a = v.canPlayType('video/mp4; codecs="hvc1.1.6.L120.B0"');
    const b = v.canPlayType('video/mp4; codecs="hev1.1.6.L120.B0"');
    _hevcSupported = !!(a || b);
  } catch {
    _hevcSupported = false;
  }
  return _hevcSupported;
}

// Render an ISO-639 language code as a friendly label. Intl.DisplayNames
// handles most cases; falls back to upper-cased code for unknown values.
let _langDisplay: { of(s: string): string | undefined } | null = null;
function friendlyLang(code: string): string {
  if (!code || code === "und") return "Unknown";
  if (!_langDisplay) {
    try {
      _langDisplay = new Intl.DisplayNames([navigator.language || "en"], { type: "language" });
    } catch {
      _langDisplay = null;
    }
  }
  try {
    const v = _langDisplay?.of(code);
    if (v && v !== code) return v;
  } catch { /* ignore */ }
  return code.toUpperCase();
}

export default function WatchPage() {
  const { kind = "live", id = "" } = useParams<{ kind: Kind; id: string }>();
  const [search] = useSearchParams();
  const { catalog, activePlaylist } = useCatalog();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastSavedAt = useRef(0);
  const [resumeAt, setResumeAt] = useState<number | undefined>();
  // Tracks whether a saved resume row exists for the current content. Drives
  // the Mark-as-watched button — there's no point offering it if we don't
  // have a row to delete. Updated by the cold-launch reconcile and cleared
  // when the user clicks Mark-as-watched.
  const [hasSavedResume, setHasSavedResume] = useState(false);
  const [catchupUrl, setCatchupUrl] = useState<string | null>(null);
  const [catchupProxy, setCatchupProxy] = useState<string | null>(null);
  const [catchupErr, setCatchupErr] = useState<string | null>(null);
  const playback = usePlayback();
  const { setSource, seekTime, setSeekTime, pickUrl, streamMode } = playback;

  // ─── Watch-page favourites toggle ───────────────────────────────────
  // Available for live/vod/catchup. Catchup folds into the live row
  // (kind='live', has_catchup=1). For series episodes the user favs the
  // series via /series/:seriesId, not from the episode watch page.
  const favKind: "live" | "vod" | null =
    kind === "live" || kind === "catchup"
      ? "live"
      : kind === "vod"
        ? "vod"
        : null;
  const favStreamId: string | null =
    favKind === "live"
      ? id  // for both live and catchup pages, id is the live stream id
      : favKind === "vod"
        ? id
        : null;
  const favIsAvailable = !!user && !!activePlaylist && favKind !== null && !!favStreamId;
  const favKey = favIsAvailable && user && activePlaylist
    ? itemKey(user.id, activePlaylist.id, favKind!, favStreamId!)
    : "";
  const [fav, setFav] = useState(false);
  useEffect(() => {
    if (!favKey) { setFav(false); return; }
    db.favorites.get(favKey).then((row) => setFav(!!row));
  }, [favKey]);
  const toggleFavOnWatch = async () => {
    if (!favIsAvailable || !user || !activePlaylist || !favKind || !favStreamId) return;
    if (fav) {
      const prev = await db.favorites.get(favKey);
      await db.favorites.delete(favKey);
      setFav(false);
      try {
        await Favorites.remove(activePlaylist.id, favKind, favStreamId);
      } catch {
        if (prev) await db.favorites.put(prev);
        setFav(true);
      }
    } else {
      const liveStream = catalog?.live.streams.find((s) => s.id === favStreamId);
      const hasCatchup =
        favKind === "live" && (liveStream ? !!liveStream.tv_archive : kind === "catchup");
      const catchupStreamId =
        hasCatchup && liveStream ? deriveCatchupStreamId(catalog, liveStream) : null;
      await db.favorites.put({
        key: favKey,
        userId: user.id,
        playlistId: activePlaylist.id,
        streamKind: favKind,
        streamId: favStreamId,
        title,
        poster: safePoster ?? null,
        hasCatchup,
        catchupStreamId,
        addedAt: Date.now(),
      });
      setFav(true);
      try {
        const saved = await Favorites.upsert(activePlaylist.id, {
          stream_kind: favKind,
          stream_id: favStreamId,
          title,
          poster: safePoster ?? null,
          has_catchup: hasCatchup,
          catchup_stream_id: catchupStreamId,
        });
        await db.favorites.put({
          key: favKey,
          serverId: saved.id,
          userId: user.id,
          playlistId: activePlaylist.id,
          streamKind: favKind,
          streamId: favStreamId,
          title: saved.title,
          poster: saved.poster,
          hasCatchup: saved.has_catchup,
          catchupStreamId: saved.catchup_stream_id,
          sortIndex: saved.sort_index,
          addedAt: Date.parse(saved.created_at) || Date.now(),
        });
      } catch {
        await db.favorites.delete(favKey);
        setFav(false);
      }
    }
  };

  // Episode + catchup program metadata come via location.state
  const lstate = location.state as {
    episode?: Episode;
    seriesTmdbId?: number | string;
    seriesName?: string;
    catchupProgram?: CatchupProgramState;
  } | null;
  const stateEpisode = lstate?.episode;
  const stateSeriesTmdbId = lstate?.seriesTmdbId;
  const catchupProgram = lstate?.catchupProgram;

  const stream = useMemo(() => {
    if (!catalog) return null;
    if (kind === "live" || kind === "catchup")
      return catalog.live.streams.find((s) => s.id === id) || null;
    if (kind === "vod") return catalog.vod.streams.find((s) => s.id === id) || null;
    return null;
  }, [catalog, kind, id]);

  // Resolve URL & engine.
  // - For "catchup" we ask the backend to construct the timeshift URL using
  //   start (YYYY-MM-DD:HH-MM) and duration (minutes) query params.
  // - For everything else we read from the catalog row.
  // The original (un-seeked) program timing — used as the anchor for
  // catchup seek-via-refetch. seekTime offsets ADD to startMs to ask the
  // panel for a stream that begins later in the program.
  const origStartMs = Number(search.get("start_ms") || "0");
  const origDurationMin = Number(search.get("duration") || "0");
  // Panel series id — only set when navigating from /series/:seriesId.
  // Stored on the resume row's `series_id` field so the Hub deep-link
  // path can call Episodes.resolve(pid, series_id, episode_id) and the
  // series-level DELETE endpoint can wipe every episode in one call.
  // Distinct from `seriesTmdbId` (location.state) which is for
  // OpenSubtitles lookup, not panel-side identification.
  const seriesPanelId = kind === "episode" ? (search.get("series") || null) : null;

  // Hub continue-watching deep-link for catchup passes the saved seek (in
  // seconds) via `?resume=N`. Apply it once on mount so the initial URL
  // is built with the shifted start. Idempotent — only fires when seekTime
  // is still 0 to avoid clobbering user-driven seeks on later renders.
  const resumeSecParam = kind === "catchup" ? Number(search.get("resume") || "0") : 0;
  const resumeAppliedCatchupRef = useRef(false);
  useEffect(() => {
    if (kind !== "catchup") return;
    if (resumeAppliedCatchupRef.current) return;
    if (resumeSecParam <= 0) return;
    resumeAppliedCatchupRef.current = true;
    setSeekTime(resumeSecParam);
  }, [kind, resumeSecParam, setSeekTime]);

  useEffect(() => {
    setCatchupErr(null);
    setCatchupUrl(null);
    setCatchupProxy(null);
    if (kind !== "catchup" || !activePlaylist) return;
    if (!origStartMs || !origDurationMin) {
      setCatchupErr("missing start_ms/duration");
      return;
    }
    const shiftedStartMs = origStartMs + Math.floor(seekTime * 1000);
    const remainingMin = Math.max(1, origDurationMin - Math.floor(seekTime / 60));
    Playlists.catchupUrl(activePlaylist.id, Number(id), shiftedStartMs, remainingMin)
      .then((d) => {
        setCatchupUrl(d.url);
        setCatchupProxy(d.proxy_url);
      })
      .catch((e) =>
        setCatchupErr(e?.response?.data?.detail || e?.message || "catchup-url failed")
      );
  }, [kind, activePlaylist, id, origStartMs, origDurationMin, seekTime]);

  let directUrl = "";
  let proxyUrl: string | null = null;
  let directAlt: string | null = null;
  let proxyAlt: string | null = null;
  let container: string | null | undefined = null;

  if (kind === "episode") {
    directUrl = stateEpisode?.url || "";
    proxyUrl = stateEpisode?.proxy_url || null;
    container = stateEpisode?.container || null;
  } else if (kind === "catchup") {
    directUrl = catchupUrl || "";
    proxyUrl = catchupProxy;
  } else {
    directUrl = stream?.url || "";
    proxyUrl = stream?.proxy_url || null;
    directAlt = stream?.url_alt || null;
    proxyAlt = stream?.proxy_url_alt || null;
    container = stream?.container || null;
  }
  // Stale-catalog safety net: legacy proxy_url like /transmux/movie/X/mkv.mp4
  // server-side redirects (307) to /playlist.m3u8, but the browser would
  // try to load it as a regular MP4 first and fail. Rewrite the URL here
  // so the engine picks hls.js up-front.
  if (proxyUrl && /\/transmux\/(movie|series)\/[^/]+\/[a-z0-9]+\.mp4(?:\?|$)/i.test(proxyUrl)) {
    proxyUrl = proxyUrl.replace(
      /(\/transmux\/(?:movie|series)\/[^/]+\/[a-z0-9]+)\.mp4/i,
      "$1/playlist.m3u8"
    );
  }

  // `directUrl` is also handed to the player as `nativeUrl`, for mpv in the
  // desktop shell. Deliberately the upstream and never the proxy/transmux URL:
  // giving mpv the source is exactly what removes this server's ffmpeg work
  // and the proxy hop for desktop viewers. Inert in a browser.
  let baseUrl = pickUrl({ directUrl, proxyUrl });
  const altUrl = directAlt ? pickUrl({ directUrl: directAlt, proxyUrl: proxyAlt }) : null;
  let isTransmux = !!proxyUrl?.includes("/transmux/");
  const isCatchup = kind === "catchup";
  const isLive = kind === "live";

  // Browser-side HEVC capability: when we know the browser can natively
  // decode hvc1, we'd rather direct-play than burn server CPU on transcoding.
  const canHevc = browserCanHevc();

  // ffprobe info (declared first so the forceTranscode probe-watcher below
  // can read it). Cleared whenever we navigate to different content.
  const [transmuxInfo, setTransmuxInfo] = useState<TransmuxInfo | null>(null);
  // Declared here rather than beside its fetch effect: the source-publishing
  // effect below needs it, and the cast receiver is unauthenticated without it.
  const [castToken, setCastToken] = useState<string | null>(null);
  useEffect(() => {
    setTransmuxInfo(null);
  }, [id, kind]);

  // forceTranscode flips on when (a) probe says the source needs server-side
  // transcoding to be playable in this browser (HEVC without browser HEVC
  // support, 10-bit H.264, exotic codecs, ...), (b) the player surfaces a
  // codec error at runtime, or (c) we've previously determined this exact
  // channel needs transcoding within the current browser session.
  //
  // Session cache rationale: once a channel has tripped the codec
  // fallback (e.g. QTV's HEVC live channels), we remember the decision
  // in sessionStorage. The cache is read SYNCHRONOUSLY during render
  // (not via useState/useEffect) — that's deliberate. On a hard refresh
  // there's a window where `activePlaylist` is null on first render
  // (catalog still hydrating); if we used a useEffect-driven flag,
  // mpegts.js would attach to the panel URL during that window, fail
  // its addSourceBuffer on HEVC, log noise, and only THEN swap to
  // hls.js after the cache check ran. Reading on every render means
  // the moment `activePlaylist` is non-null, the URL is built with
  // forceTranscode=true and mpegts.js never sees the panel URL.
  // Cleared by closing the tab — deliberate so a panel that re-encodes
  // its feed to H.264 isn't permanently stuck on the transmux path.
  const transcodeCacheKey = activePlaylist && id
    ? `brainzr.transcode:${activePlaylist.id}:${kind}:${id}`
    : null;
  const cachedNeedsTranscode = transcodeCacheKey && typeof window !== "undefined"
    ? window.sessionStorage.getItem(transcodeCacheKey) === "1"
    : false;
  // Synchronous container check — done at render time (not via useEffect)
  // so the very first URL we hand to the Player is already the transmux
  // URL. Otherwise there's a one-render window where we'd hand the player
  // a raw .mkv URL the browser can't decode, the engine errors, and the
  // user sees a flash of "Unable to play" before the auto-fallback kicks
  // in. The backend `proxy_url` is now always raw (Android/ExoPlayer-
  // friendly); the web flips to /transmux/* here when needed.
  const containerNeedsTranscode = containerNeedsTranscodeFor(kind, container);
  const [runtimeForceTranscode, setRuntimeForceTranscode] = useState(false);
  // Reset runtime flag when content changes; cache flag follows the URL.
  useEffect(() => {
    setRuntimeForceTranscode(false);
  }, [id, kind]);
  // Persist runtime trips so future plays of the same channel use the
  // transmux path from the start.
  useEffect(() => {
    if (typeof window === "undefined" || !transcodeCacheKey) return;
    if (runtimeForceTranscode) {
      window.sessionStorage.setItem(transcodeCacheKey, "1");
    }
  }, [runtimeForceTranscode, transcodeCacheKey]);
  const forceTranscode = runtimeForceTranscode || cachedNeedsTranscode || containerNeedsTranscode;
  const setForceTranscode = setRuntimeForceTranscode;

  // Probe-driven proactive routing — saves the user from seeing a flash of
  // "format not supported" error before the auto-fallback (HEVC, 10-bit, etc).
  useEffect(() => {
    if (isLive || isCatchup) return;
    if (!transmuxInfo) return;
    const isHevc = (transmuxInfo.video_codec || "").toLowerCase() === "hevc";
    if (transmuxInfo.needs_video_transcode && !(isHevc && canHevc)) {
      setForceTranscode(true);
    }
  }, [transmuxInfo, isLive, isCatchup, canHevc]);

  const handleError = (msg: string) => {
    if (forceTranscode) return;
    // For live we now also flip to transmux on AC-3 / E-AC-3 audio (Chrome
    // can't decode them in MSE) and on any addSourceBuffer rejection,
    // since in our environment that's almost always a codec the browser
    // doesn't support and ffmpeg can fix it.
    if (isLive) {
      if (
        /hevc|h\.?265|hvc1|hev1/i.test(msg) ||
        /\bac-?3\b|\bec-?3\b|\beac-?3\b/i.test(msg) ||
        /he-?aac|mp4a\.40\.(?:5|29)/i.test(msg) ||
        /bufferappenderror/i.test(msg) ||
        /addsourcebuffer/i.test(msg) ||
        /panel keeps dropping/i.test(msg) ||
        /isn't supported|not supported|unsupported/i.test(msg)
      ) {
        setForceTranscode(true);
      }
      return;
    }
    // For VOD/episode any codec/format error → route through transmux.
    // The disk cache means subsequent viewers don't pay the encode cost.
    if (
      /hevc|h\.?265|hvc1|hev1/i.test(msg) ||
      /\bac-?3\b|\bec-?3\b|\beac-?3\b/i.test(msg) ||
      /he-?aac|mp4a\.40\.(?:5|29)/i.test(msg) ||
      /bufferappenderror/i.test(msg) ||
      /isn't supported|not supported|unsupported|MEDIA_ERR_SRC_NOT_SUPPORTED/i.test(msg) ||
      /addsourcebuffer/i.test(msg)
    ) {
      setForceTranscode(true);
    }
  };

  // Apply the override.
  if (forceTranscode && activePlaylist && id) {
    if (isLive) {
      // HLS playlist (sliding window) instead of a TS pipe — the pipe
      // delivers bytes at exactly realtime so the client can never build
      // a real buffer; HLS lets hls.js read segments off disk faster than
      // realtime and accumulate ~30s of forward cushion.
      baseUrl = `/api/playlists/${activePlaylist.id}/transmux/live/${encodeURIComponent(id)}/playlist.m3u8`;
      isTransmux = true;
    } else if (kind === "vod") {
      const ext = container || "mp4";
      // HLS playlist endpoint — hls.js gives native scrubbing inside the
      // produced segments, no seek-via-reload, and concurrent viewers
      // share the same disk cache.
      baseUrl = `/api/playlists/${activePlaylist.id}/transmux/movie/${encodeURIComponent(id)}/${encodeURIComponent(ext)}/playlist.m3u8`;
      isTransmux = true;
    } else if (kind === "episode" && stateEpisode) {
      const ext = container || "mkv";
      baseUrl = `/api/playlists/${activePlaylist.id}/transmux/series/${encodeURIComponent(String(stateEpisode.id))}/${encodeURIComponent(ext)}/playlist.m3u8`;
      isTransmux = true;
    }
  }
  // Transmux mode: progress is reported through `baseTime`, slider seeking
  // is delegated up here so we can rebuild the URL with `?seek=N` and let
  // ffmpeg restart at the requested time. seekTime lives in PlaybackContext
  // so that mini-player → watch round-trips preserve playhead position.
  // (transmuxInfo is declared earlier so the forceTranscode logic can read it.)

  // Probe ALL VOD/episode content (not just MKV transmux) so we can pull
  // out embedded subtitle tracks even from regular MP4 movies. The probe
  // call also gives us duration for transmux which the piped fMP4 lacks.
  useEffect(() => {
    if (!activePlaylist) return;
    if (kind === "vod") {
      const ext = container || "mp4";
      Transmux.movieInfo(activePlaylist.id, Number(id), ext)
        .then((info) => setTransmuxInfo(info))
        .catch(() => setTransmuxInfo(null));
    } else if (kind === "episode" && stateEpisode) {
      const ext = container || "mkv";
      Transmux.episodeInfo(activePlaylist.id, Number(stateEpisode.id), ext)
        .then((info) => setTransmuxInfo(info))
        .catch(() => setTransmuxInfo(null));
    }
  }, [activePlaylist, id, kind, container, stateEpisode]);

  // Transmux is now HLS (playlist.m3u8 + segments) — hls.js handles native
  // scrubbing inside the produced segments, so we no longer need the
  // seek-via-reload `?seek=N` URL rewrite. Catchup still uses reload because
  // its TS stream has to be re-requested with a shifted start_ms.
  const url = baseUrl;
  // Cast URL: for live channels we always route through the cast HLS
  // transcode endpoint so the receiver gets H.264 + AAC (default cast
  // can't decode HEVC, can't read raw .ts, and the upstream HLS alt is
  // sometimes missing or also HEVC).
  const castUrl =
    isLive && activePlaylist && id
      ? `/api/playlists/${activePlaylist.id}/transmux/cast/${encodeURIComponent(id)}/playlist.m3u8`
      : null;

  // OpenSubtitles integration — fetch metadata once we know the panel's
  // tmdb_id and the operator has configured an API key.
  const [openSubsOk, setOpenSubsOk] = useState<boolean>(false);
  useEffect(() => {
    Subs.configured()
      .then((c) => setOpenSubsOk(c.ok))
      .catch(() => setOpenSubsOk(false));
  }, []);
  const [tmdbId, setTmdbId] = useState<number | null>(null);
  useEffect(() => {
    setTmdbId(null);
    if (!activePlaylist || !openSubsOk) return;
    if (kind === "vod") {
      Vod.info(activePlaylist.id, Number(id))
        .then((v) => {
          const t = v?.info?.tmdb_id;
          if (!t) return;
          const n = typeof t === "number" ? t : parseInt(String(t), 10);
          if (Number.isFinite(n) && n > 0) setTmdbId(n);
        })
        .catch(() => { /* leave null */ });
    } else if (kind === "episode" && stateSeriesTmdbId) {
      const n = typeof stateSeriesTmdbId === "number"
        ? stateSeriesTmdbId
        : parseInt(String(stateSeriesTmdbId), 10);
      if (Number.isFinite(n) && n > 0) setTmdbId(n);
    }
  }, [activePlaylist, kind, id, openSubsOk, stateSeriesTmdbId]);

  const [openSubs, setOpenSubs] = useState<OpenSubtitleResult[]>([]);
  useEffect(() => {
    setOpenSubs([]);
    if (!tmdbId) return;
    // For episodes, narrow the search by season + episode so we get the
    // right subs, not subs for some other episode of the same show.
    const params: { tmdb_id: number; season?: number; episode?: number } = { tmdb_id: tmdbId };
    if (kind === "episode" && stateEpisode) {
      const sNum = parseInt(String(stateEpisode.season), 10);
      const eNum = parseInt(String(stateEpisode.episode_num), 10);
      if (Number.isFinite(sNum)) params.season = sNum;
      if (Number.isFinite(eNum)) params.episode = eNum;
    }
    Subs.search(params)
      .then((r) => setOpenSubs(r.results || []))
      .catch(() => setOpenSubs([]));
  }, [tmdbId, kind, stateEpisode]);

  // Embedded subs (from the source file via ffprobe) — separate list shown
  // alongside OpenSubtitles in the modal.
  const embeddedSubs = useMemo(() => {
    if (!transmuxInfo?.subtitles?.length || !activePlaylist) return [];
    let prefix: string | null = null;
    if (kind === "vod") {
      const ext = container || "mp4";
      prefix = `/api/playlists/${activePlaylist.id}/transmux/movie/${encodeURIComponent(id)}/${encodeURIComponent(ext)}/subtitle`;
    } else if (kind === "episode" && stateEpisode) {
      const ext = container || "mkv";
      prefix = `/api/playlists/${activePlaylist.id}/transmux/series/${encodeURIComponent(stateEpisode.id)}/${encodeURIComponent(ext)}/subtitle`;
    }
    if (!prefix) return [];
    return transmuxInfo.subtitles.map((s) => ({
      url: `${prefix}/${s.index}.vtt`,
      lang: s.lang,
      label: `${s.title || friendlyLang(s.lang)} (embedded)`,
    }));
  }, [transmuxInfo, activePlaylist, kind, id, container, stateEpisode]);

  // Currently chosen subtitle — only ONE active at a time. User picks via
  // the modal browser. Reset on content change.
  const [chosenSub, setChosenSub] = useState<PlaybackSubtitle | null>(null);
  useEffect(() => { setChosenSub(null); }, [id, kind]);
  const subtitleTracks = useMemo(
    () => (chosenSub ? [chosenSub] : undefined),
    [chosenSub]
  );

  const [subsBrowserOpen, setSubsBrowserOpen] = useState(false);
  // Show the captions button (which opens the modal) whenever there's
  // ANYTHING to pick: OS results OR embedded subs.
  const hasPickableSubs = openSubs.length > 0 || embeddedSubs.length > 0;
  // Stable identifier for "this content item" — survives URL rewrites caused
  // by seek-via-reload, catchup refetch, transcode fallback. PlaybackContext
  // uses this to decide whether to reset position/seek on a re-publish.
  const contentKey = `${kind}:${id}:${origStartMs || ""}`;
  // Engine selection: any URL pointing at our HLS playlist endpoint must
  // use hls.js, regardless of container/extension hints. This covers both
  // the catalog-routed MKV → /playlist.m3u8 case and the forceTranscode
  // override that swaps in an HLS URL.
  let engine: Engine = pickEngine(kind, container, directUrl);
  if (url.includes("/playlist.m3u8") || url.endsWith(".m3u8")) {
    engine = "hls";
  }
  const altEngine: Engine | undefined = directAlt
    ? pickEngine(kind, "m3u8", directAlt)
    : undefined;
  const title = (kind === "catchup" && catchupProgram?.title) || stream?.name || stateEpisode?.title || "";
  const safePoster = safeImg(stream?.logo || stream?.cover || stateEpisode?.still || null) || null;
  // Catchup has no duration metadata at all, so it is passed explicitly.
  //
  // Transmuxed VOD needs it too, and the note that used to sit here — "HLS
  // playlists carry segment durations in #EXTINF so video.duration is
  // accurate" — is only true once ffmpeg has finished and written
  // #EXT-X-ENDLIST. While it is still working, the playlist lists only the
  // segments produced so far, so video.duration reports *how much has been
  // transmuxed*: a two-hour film opened cold showed 21:49 and crept upward,
  // with the scrub bar unable to reach the rest. The probe already fetched
  // above carries the real runtime, so use it.
  const forceDuration =
    kind === "catchup"
      ? Number(search.get("duration") || "0") * 60
      : isTransmux && transmuxInfo?.duration_s
        ? transmuxInfo.duration_s
        : null;

  // Restore resume position (vod + series only). Both transmux-HLS and
  // direct-play paths now support native seeking, so the saved position
  // becomes resumeAt (passed to Player → set on loadedmetadata).
  //
  // Two-stage read: local cache (instant, never blocks playback) → then
  // server reconcile in the background. If the server has a later
  // position (user watched ahead on another device since last sync) we
  // silently fast-forward via setResumeAt; the player's onProgress hook
  // will catch the new position up to current playback. We never *rewind*
  // on cold launch — server going backward (e.g. from a manual reset)
  // would interrupt mid-watch otherwise.
  const resumeAppliedRef = useRef<string>("");
  useEffect(() => {
    if (!user || !activePlaylist || isLive || isCatchup || !id) return;
    const key = `${kind}::${id}`;
    if (resumeAppliedRef.current === key) return;
    if (!proxyUrl && !directUrl) return;  // wait until URL resolution settles
    resumeAppliedRef.current = key;
    const resumeKind: "vod" | "series" = kind === "episode" ? "series" : "vod";
    const k = itemKey(user.id, activePlaylist.id, resumeKind, id);
    let appliedLocal = 0;
    db.resume.get(k).then((row) => {
      const pos = row?.position;
      if (pos && pos >= 5) {
        appliedLocal = pos;
        setResumeAt(pos);
        setHasSavedResume(true);
      }
    });
    // Background reconcile against the server. Only fast-forward.
    // We also mirror the WHOLE list into Dexie here so any future
    // "Continue watching" surface reads a fresh local cache.
    Resume.list(activePlaylist.id)
      .then(async (items) => {
        if (user) await syncResumeCache(user.id, activePlaylist.id, items);
        const match = items.find(
          (s) => s.stream_kind === resumeKind && s.stream_id === id,
        );
        if (!match) return;
        const serverPos = match.position_ms / 1000;
        if (serverPos > appliedLocal + 5 && serverPos >= 5) {
          setResumeAt(serverPos);
        }
        if (serverPos >= 5) setHasSavedResume(true);
      })
      .catch(() => { /* offline → local cache wins */ });
  // proxyUrl + directUrl listed via boolean coercion to dampen ref churn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activePlaylist, isLive, isCatchup, id, kind, !!proxyUrl, !!directUrl]);

  // Publish the currently-playing source to PlaybackContext so MiniPlayer can
  // pick it up when the user navigates away from /watch.
  useEffect(() => {
    if (!url) return;
    // Transmux now uses HLS with native scrubbing → "native". Catchup still
    // needs reload-via-refetch because the panel's TS stream restarts at the
    // shifted start_ms.
    const seekMode = isCatchup ? "reload-catchup" : "native";
    setSource({
      url,
      altUrl,
      engine,
      altEngine,
      poster: safePoster,
      title,
      isLive,
      forceDuration,
      href: location.pathname + location.search,
      contentKey,
      seekMode,
      // baseProxyUrl was only used by the old reload-transmux mode; HLS
      // transmux doesn't need it.
      baseProxyUrl: undefined,
      catchupCtx:
        seekMode === "reload-catchup" && activePlaylist
          ? {
              playlistId: activePlaylist.id,
              streamId: Number(id),
              origStartMs,
              durationMin: origDurationMin,
            }
          : undefined,
      castUrl,
      castToken,
      subtitles: subtitleTracks,
    });
  }, [url, altUrl, engine, altEngine, safePoster, title, isLive, forceDuration, location.pathname, location.search, setSource, contentKey, isTransmux, isCatchup, proxyUrl, activePlaylist, id, origStartMs, origDurationMin, castUrl, castToken, subtitleTracks, streamMode]);

  // Cast token — mint when we have an active xtream playlist (cast receiver
  // can't reach our origin with cookies; the token is embedded in cast URLs
  // and the rewritten HLS playlist).
  useEffect(() => {
    setCastToken(null);
    if (!activePlaylist || activePlaylist.kind !== "xtream") return;
    CastApi.token(activePlaylist.id)
      .then((t) => setCastToken(t))
      .catch(() => setCastToken(null));
  }, [activePlaylist]);

  // EPG now/next (live only, xtream only) — pull a longer window so "Up next"
  // has something to show even if get_short_epg is sparse.
  const [epg, setEpg] = useState<ShortEpgEntry[]>([]);
  useEffect(() => {
    setEpg([]);
    if (!isLive || !activePlaylist || !stream || !id || activePlaylist.kind !== "xtream") return;
    Playlists.shortEpg(activePlaylist.id, Number(id), 12)
      .then((d) => setEpg(d?.epg_listings || []))
      .catch(() => setEpg([]));
  }, [isLive, activePlaylist, stream, id]);

  // Mark-as-watched: nukes the saved resume row for this stream. Only
  // exposed when there's actually a row to delete (hasSavedResume), and
  // only on resumable kinds (vod / episode / catchup — not live). Click
  // is best-effort: we update local state immediately so the button
  // disappears, then DELETE on the server. Failure rolls back the flag.
  const markAsWatched = async () => {
    if (!user || !activePlaylist) return;
    if (isLive) return;
    let resumeKind: "vod" | "series" | "catchup";
    let resumeStreamId: string;
    if (kind === "catchup") {
      if (!origStartMs) return;
      resumeKind = "catchup";
      resumeStreamId = `${id}_${origStartMs}`;
    } else if (kind === "episode") {
      resumeKind = "series";
      resumeStreamId = id;
    } else {
      resumeKind = "vod";
      resumeStreamId = id;
    }
    const k = itemKey(user.id, activePlaylist.id, resumeKind, resumeStreamId);
    const prev = await db.resume.get(k);
    setHasSavedResume(false);
    setResumeAt(undefined);
    void db.resume.delete(k);
    try {
      await Resume.remove(activePlaylist.id, resumeKind, resumeStreamId);
    } catch {
      // Roll back local cache if the server call failed; keep the button
      // hidden though — the user explicitly clicked Mark-as-watched.
      if (prev) await db.resume.put(prev);
    }
  };

  // pagehide-flush wiring lives once at module-load level via this hook —
  // the resume module is self-idempotent, so calling on every WatchPage
  // mount is fine. Without this the user's last few seconds before tab
  // close are lost.
  useEffect(() => { ensurePageHideFlush(); }, []);

  // Once a stream crosses ~95 % we DELETE the row server-side so the
  // "Continue watching" carousel doesn't surface stuff the user already
  // finished. This ref dedupes — we only fire one DELETE per content key.
  const finishedRef = useRef<string>("");

  const onProgress = (currentTime: number, duration: number) => {
    // Always feed PlaybackContext so MiniPlayer can pick up where we left off.
    playback.reportPosition(currentTime);
    if (isLive || !user || !activePlaylist) return;
    const now = Date.now();
    if (now - lastSavedAt.current < 5000) return;
    lastSavedAt.current = now;

    // Decide the resume_kind + the stream_id we key the row on.
    //   vod      → ("vod", id)
    //   episode  → ("series", id)            // id is the episode id
    //   catchup  → ("catchup", `${liveId}_${origStartMs}`)
    //              the "stream_id" is a synthetic token unique to this
    //              programme airing; redundant with catchup_channel_id +
    //              catchup_start_ms but lets the same UNIQUE constraint
    //              on (user, playlist, kind, stream_id) allow multiple
    //              programmes per channel.
    let resumeKind: "vod" | "series" | "catchup";
    let resumeStreamId: string;
    if (kind === "catchup") {
      if (!origStartMs || !origDurationMin) return;  // missing metadata
      resumeKind = "catchup";
      resumeStreamId = `${id}_${origStartMs}`;
    } else if (kind === "episode") {
      resumeKind = "series";
      resumeStreamId = id;
    } else {
      resumeKind = "vod";
      resumeStreamId = id;
    }
    const k = itemKey(user.id, activePlaylist.id, resumeKind, resumeStreamId);

    // 95 % terminal cleanup. Only a 30 s+ stream qualifies (matches the
    // legacy MIN_RESUME_SECONDS rule); below that the user can't have
    // meaningfully consumed it. Local row goes too so the local cache
    // doesn't pretend there's a position to resume from.
    const ratio = duration > 0 ? currentTime / duration : 0;
    const finishKey = `${resumeKind}::${resumeStreamId}`;
    if (duration >= 30 && ratio >= 0.95 && finishedRef.current !== finishKey) {
      finishedRef.current = finishKey;
      void db.resume.delete(k);
      Resume.remove(activePlaylist.id, resumeKind, resumeStreamId).catch(() => {
        /* best-effort */
      });
      return;
    }

    // Resolve the channel name + programme title for catchup rows. Without
    // these the Hub continue-watching surface can't render a useful card.
    const catchupChannelName = kind === "catchup"
      ? (stream?.name || null)
      : null;
    const catchupProgrammeTitle = kind === "catchup"
      ? (catchupProgram?.title || null)
      : null;

    // Local write-through cache: keep the row freshest — cold launch and
    // cross-page navigation (e.g. MiniPlayer) read from here without
    // waiting for the server.
    db.resume.put({
      key: k,
      userId: user.id,
      playlistId: activePlaylist.id,
      streamKind: resumeKind,
      streamId: resumeStreamId,
      title,
      poster: stream?.logo || stream?.cover || null,
      position: currentTime,
      duration,
      seriesId: seriesPanelId,
      catchupChannelId: kind === "catchup" ? id : null,
      catchupStartMs: kind === "catchup" ? origStartMs : null,
      catchupDurationMin: kind === "catchup" ? origDurationMin : null,
      catchupChannelName,
      catchupProgrammeTitle,
      updatedAt: now,
    });

    // Server-side debounced upsert. The debouncer coalesces sub-10s
    // updates; on tab close `flushPendingResume()` blasts the latest via
    // sendBeacon so we don't lose progress.
    const minMs = duration >= 30 ? 30_000 : 0;  // gate trivial scrubs
    const posMs = Math.floor(currentTime * 1000);
    if (posMs >= minMs) {
      scheduleResumeUpsert(activePlaylist.id, {
        stream_kind: resumeKind,
        stream_id: resumeStreamId,
        title,
        poster: stream?.logo || stream?.cover || null,
        position_ms: posMs,
        duration_ms: Math.floor(duration * 1000),
        series_id: seriesPanelId,
        catchup_channel_id: kind === "catchup" ? id : null,
        catchup_start_ms: kind === "catchup" ? origStartMs : null,
        catchup_duration_min: kind === "catchup" ? origDurationMin : null,
        catchup_channel_name: catchupChannelName,
        catchup_programme_title: catchupProgrammeTitle,
      });
    }
  };

  // When the player unmounts (back, navigate, playlist switch), flush
  // any pending position immediately so the next device sees it.
  useEffect(() => {
    return () => { flushPendingResume(); };
  }, []);

  if (!activePlaylist || !catalog) {
    return (
      <PageLayout>
        <EmptyState title="Loading…" hint="Hold tight while we load the catalog." />
      </PageLayout>
    );
  }
  if (catchupErr) {
    return (
      <PageLayout>
        <EmptyState title="Catchup unavailable" hint={catchupErr} />
      </PageLayout>
    );
  }
  if (kind === "catchup" && !catchupUrl) {
    return (
      <PageLayout>
        <EmptyState title="Loading catchup…" hint="Resolving the timeshift URL." />
      </PageLayout>
    );
  }
  if (!url) {
    return (
      <PageLayout>
        <EmptyState title="Stream not found" hint={`We couldn't locate the ${kind} you asked for.`} />
      </PageLayout>
    );
  }

  // EPG times — Xtream returns either UTC strings or unix-second timestamps
  const tz = getTimezone();
  function startOf(p: ShortEpgEntry): number {
    const v = p.start_timestamp ?? p.start;
    if (typeof v === "number") return v * 1000;
    if (typeof v === "string") {
      if (/^\d{10}$/.test(v)) return Number(v) * 1000;
      const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(v);
      if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    }
    return NaN;
  }
  function endOf(p: ShortEpgEntry): number {
    const v = p.stop_timestamp ?? p.end;
    if (typeof v === "number") return v * 1000;
    if (typeof v === "string") {
      if (/^\d{10}$/.test(v)) return Number(v) * 1000;
      const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(v);
      if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    }
    return NaN;
  }

  const now = Date.now();
  const epgRanged = epg
    .map((p) => ({ p, start: startOf(p), end: endOf(p) }))
    .filter((x) => Number.isFinite(x.start) && Number.isFinite(x.end));
  const nowEntry = epgRanged.find((x) => x.start <= now && now < x.end) || null;
  const nextEntries = epgRanged.filter((x) => x.start > now).slice(0, 4);
  // "Up next" means literally what's next — the currently-playing program
  // is rendered in the "On now" card above, so we don't repeat it here.
  const upcomingForRail = nextEntries;

  // Progress through the current program (for the live mini progress bar)
  const nowProgress = nowEntry
    ? Math.max(0, Math.min(1, (now - nowEntry.start) / (nowEntry.end - nowEntry.start)))
    : 0;

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-3 text-[13px] font-ui transition-fast"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div>
            <div className="rounded-xl overflow-hidden bg-black">
              <Player
                url={url}
                altUrl={altUrl}
                engine={engine}
                altEngine={altEngine}
                poster={safePoster}
                title={title}
                isLive={isLive}
                forceDuration={forceDuration}
                nativeUrl={directUrl || null}
                castToken={castToken}
                castUrl={castUrl}
                subtitles={subtitleTracks}
                resumeAt={resumeAt}
                baseTime={isCatchup ? seekTime : 0}
                onSeek={
                  isCatchup ? (t) => setSeekTime(Math.max(0, t)) : undefined
                }
                seekable={true}
                onError={handleError}
                onProgress={onProgress}
                onOpenSubsBrowser={hasPickableSubs ? () => setSubsBrowserOpen(true) : undefined}
              />
            </div>

            <div className="mt-3 flex items-start gap-3">
              <h1 className="flex-1 font-display text-[26px] sm:text-[34px] tracking-[-0.5px] text-text-primary">
                {title}
              </h1>
              {!isLive && hasSavedResume && (
                <button
                  type="button"
                  onClick={markAsWatched}
                  className="mt-1 h-11 px-3 inline-flex items-center gap-1.5 rounded-full bg-surface/80 backdrop-blur-sm border border-muted/40 hover:border-accent-gold/60 transition-fast text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary hover:text-text-primary"
                  aria-label="Mark as watched"
                  title="Mark as watched — removes from Continue Watching"
                >
                  <CheckCircle2 size={16} className="text-accent-gold" />
                  Mark as watched
                </button>
              )}
              {favIsAvailable && (
                <button
                  type="button"
                  onClick={toggleFavOnWatch}
                  className="mt-1 w-11 h-11 flex items-center justify-center rounded-full bg-surface/80 backdrop-blur-sm border border-muted/40 hover:border-accent-gold/60 transition-fast"
                  aria-label={fav ? "Remove from favourites" : "Add to favourites"}
                  title={fav ? "Remove from favourites" : "Add to favourites"}
                >
                  <Heart size={18} className={fav ? "fill-danger text-danger" : "text-text-secondary"} />
                </button>
              )}
            </div>

            {/* Movie info card — year / rating / genre / plot. Pulled from
                the catalog row (stream) so it renders without an extra API
                call. Hidden when there's nothing to show. */}
            {kind === "vod" && stream && (() => {
              const ratingTxt = formatRating(stream.rating);
              const hasMeta = !!(stream.year || ratingTxt || stream.genre);
              if (!hasMeta && !stream.plot) return null;
              return (
                <div className="mt-2 bg-surface rounded-xl border border-muted/30 p-3">
                  {hasMeta && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-[0.06em] text-accent-gold mb-1.5">
                      {stream.year && <span>{stream.year}</span>}
                      {ratingTxt && <span>★ {ratingTxt}</span>}
                      {stream.genre && (
                        <span className="text-text-secondary normal-case tracking-normal">
                          {stream.genre}
                        </span>
                      )}
                    </div>
                  )}
                  {stream.plot && (
                    <p className="text-[13px] font-ui text-text-secondary leading-relaxed">
                      {stream.plot}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Episode info — series name + S/E label + episode plot. Series
                name comes via location.state from /series/:seriesId. */}
            {kind === "episode" && stateEpisode && (
              <div className="mt-2 bg-surface rounded-xl border border-muted/30 p-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-[0.06em] text-accent-gold mb-1.5">
                  <span>S{stateEpisode.season}E{stateEpisode.episode_num}</span>
                  {lstate?.seriesName && (
                    <span className="text-text-secondary normal-case tracking-normal">
                      {lstate.seriesName}
                    </span>
                  )}
                </div>
                {stateEpisode.plot && (
                  <p className="text-[13px] font-ui text-text-secondary leading-relaxed">
                    {stateEpisode.plot}
                  </p>
                )}
              </div>
            )}

            {/* Catchup: show what the user is watching (program description,
                aired-at time, original duration). */}
            {kind === "catchup" && catchupProgram && (
              <div className="mt-2 bg-surface rounded-xl border border-muted/30 p-3">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.08em] text-accent-gold mb-1">
                  Catchup
                  {catchupProgram.startMs && catchupProgram.endMs && (
                    <span className="text-text-secondary normal-case tracking-normal">
                      Aired {fmtTimeIn(catchupProgram.startMs, tz)} – {fmtTimeIn(catchupProgram.endMs, tz)} ({tz})
                    </span>
                  )}
                </div>
                {catchupProgram.description && (
                  <p className="text-[13px] font-ui text-text-secondary leading-relaxed">
                    {catchupProgram.description}
                  </p>
                )}
              </div>
            )}

            {/* Now playing (compact, immediately visible under the title) */}
            {isLive && nowEntry && (
              <div className="mt-2 bg-surface rounded-xl border border-muted/30 p-3">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.08em] text-accent-gold mb-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-gold animate-live-pulse" />
                  On now
                  <span className="text-text-secondary normal-case tracking-normal">
                    {fmtTimeIn(nowEntry.start, tz)} – {fmtTimeIn(nowEntry.end, tz)} ({tz})
                  </span>
                </div>
                <p className="text-[15px] font-ui font-medium text-text-primary">
                  {decodeMaybe(nowEntry.p.title)}
                </p>
                {nowEntry.p.description && (
                  <p className="text-[12px] font-ui text-text-secondary mt-1 line-clamp-2">
                    {decodeMaybe(nowEntry.p.description)}
                  </p>
                )}
                <div className="mt-2 h-1 rounded-pill bg-muted/30 overflow-hidden">
                  <div
                    className="h-full bg-accent-gold"
                    style={{ width: `${Math.round(nowProgress * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* EPG rail — sticky next to player on desktop, stacks below on mobile */}
          {isLive && upcomingForRail.length > 0 && (
            <aside className="lg:max-h-[80vh] lg:overflow-y-auto no-scrollbar">
              <h2 className="font-ui font-semibold text-[12px] uppercase tracking-[0.06em] text-text-secondary mb-2 flex items-center gap-2">
                Up next
                <span className="text-text-muted normal-case tracking-normal">({tz})</span>
              </h2>
              <div className="space-y-2">
                {upcomingForRail.map((x, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border bg-surface border-muted/30 p-3"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-[0.06em] text-accent-gold mb-1">
                      {fmtTimeIn(x.start, tz)} – {fmtTimeIn(x.end, tz)}
                    </p>
                    <p className="text-[13px] font-ui font-medium text-text-primary">
                      {decodeMaybe(x.p.title)}
                    </p>
                    {x.p.description && (
                      <p className="text-[11px] font-ui text-text-secondary mt-1 line-clamp-2">
                        {decodeMaybe(x.p.description)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>
      <SubtitleBrowser
        open={subsBrowserOpen}
        onClose={() => setSubsBrowserOpen(false)}
        results={openSubs}
        embedded={embeddedSubs}
        currentUrl={chosenSub?.url ?? null}
        onPick={(s) => setChosenSub(s)}
      />
    </PageLayout>
  );
}

